import { useMemo, useState } from 'react';
import { useNotifications } from '@/entities/notification';
import { useMarkNotificationRead } from '@/features/notification/mark-read';
import type { Notification, PromotionalRoGrantData } from '@/entities/notification';
import { PromotionalGrantModal } from './PromotionalGrantModal';

interface QueueItem {
  notificationId: string;
  data: PromotionalRoGrantData;
}

const parseGrantData = (notification: Notification): PromotionalRoGrantData | null => {
  const d = notification.data;
  if (!d || typeof d !== 'object') {
    return null;
  }
  const { amount, reason, newAllowance } = d as Partial<PromotionalRoGrantData>;
  if (typeof amount !== 'number' || typeof reason !== 'string' || typeof newAllowance !== 'number') {
    return null;
  }
  return { amount, reason, newAllowance };
};

export const PromotionalGrantModalHost = () => {
  const { notifications } = useNotifications();
  const { mutate: markRead } = useMarkNotificationRead();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());

  const queue = useMemo<QueueItem[]>(() => {
    return notifications
      .filter((n) => n.type === 'promotional_ro_grant' && !n.isRead && !dismissedIds.has(n.id))
      .map((n) => {
        const data = parseGrantData(n);
        return data ? { notificationId: n.id, data } : null;
      })
      .filter((x): x is QueueItem => x !== null);
  }, [notifications, dismissedIds]);

  const current = queue[0] ?? null;

  const handleClose = () => {
    if (!current) {
      return;
    }
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(current.notificationId);
      return next;
    });
    markRead({ id: current.notificationId });
  };

  return <PromotionalGrantModal open={current !== null} data={current?.data ?? null} onClose={handleClose} />;
};
